import { Table } from "components/Common/Table/Table";
import React, { MouseEvent, useState } from "react";
import { KebabIcon } from "assets/icons";
import Button from "components/foundation/Button";
import styled from "styled-components";
import { match } from "ts-pattern";
import useMediaView from "features/common/hooks/useMediaView";
import dayjs from "dayjs";
import BottomSheet from "components/foundation/BottomSheet/BottomSheet";
import downloadPDF from "utils/downloadPdf";
import copyToClipboard from "utils/copyClipboard";
import Toast from "components/foundation/Toast";
import { useNavigate } from "react-router";
import { ROUTER_PATH } from "config/path";
import CheckBox from "components/foundation/CheckBox/CheckBox";
import openPdfPreview from "utils/openPdfPreview";

interface ContractTableRowProps {
  id: string;
  no: number;
  pj: string;
  businessType: string;
  informationProvider: string;
  author: string;
  submissionDate: string;
  documentStatus: string;
  documentFileUrl?: string;
  isSelected?: boolean;
  onCheckBoxClick?: (id: string, documentFileUrl?: string) => void;
}

const ContractTableRow = ({
  id,
  no,
  pj,
  businessType,
  informationProvider,
  author,
  submissionDate,
  documentStatus,
  documentFileUrl,
  isSelected = false,
  onCheckBoxClick,
}: ContractTableRowProps) => {
  const isSmall = useMediaView("small");
  const [isEtcOpen, setIsEtcOpen] = useState(false);
  const [isToastOpen, setIsToastOpen] = useState(false);
  const navigate = useNavigate();

  const handleOpenPdf = (e: MouseEvent<HTMLDivElement | HTMLButtonElement>) => {
    e.stopPropagation();
    openPdfPreview(documentFileUrl);
  };

  const handleDownloadPdf = (
    e: MouseEvent<HTMLDivElement | HTMLButtonElement>,
  ) => {
    e.stopPropagation();
    documentFileUrl && downloadPDF(documentFileUrl, formattedSubmissionDate);
  };

  const handleCopyLink = (
    e: MouseEvent<HTMLDivElement | HTMLButtonElement>,
  ) => {
    e.stopPropagation();
    documentFileUrl && copyToClipboard(documentFileUrl);
    setIsToastOpen(true);
  };

  const formattedSubmissionDate = dayjs(submissionDate).format("YYYY-MM-DD");

  const handleDetailClick = () => {
    navigate(ROUTER_PATH.document.analysis.detail(id));
  };

  const handlePdfBottomSheetOpenClick = (e: MouseEvent<HTMLOrSVGElement>) => {
    e.stopPropagation();
    e.preventDefault();
    setIsEtcOpen(true);
  };

  const handleCheckBoxClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    onCheckBoxClick?.(id, documentFileUrl);
  };

  const documentStatusText =
    documentStatus === "REVIEW" ? "1차 등록" : "검토 완료";

  const truncate = (str: string, max: number) =>
    str && str.length > max ? `${str.slice(0, max)}…` : str;

  return (
    <Table.Tr
      onClick={handleDetailClick}
      style={{
        cursor: "pointer",
      }}
    >
      {match(isSmall)
        .with(true, () => (
          <>
            <Table.Td>{pj}</Table.Td>
            <Table.Td>{formattedSubmissionDate}</Table.Td>
            <Table.Td>{documentStatusText}</Table.Td>
            <Table.Td
              style={{
                margin: "auto",
              }}
            >
              {documentFileUrl && (
                <KebabIcon onClick={handlePdfBottomSheetOpenClick} />
              )}
              <Toast.Message
                open={isToastOpen}
                onOpenChange={(open) => setIsToastOpen(open)}
              >
                링크가 복사되었습니다.
              </Toast.Message>
            </Table.Td>
            <BottomSheet title="PDF" isOpen={isEtcOpen} setOpen={setIsEtcOpen}>
              <div onClick={handleOpenPdf}>조회</div>
              <div onClick={handleDownloadPdf}>다운로드</div>
              <div onClick={handleCopyLink}>링크복사</div>
            </BottomSheet>
          </>
        ))
        .otherwise(() => (
          <>
            <Table.Td
              style={{
                width: "100%",
                display: "flex",
                justifyContent: "center",
              }}
            >
              <CheckBox
                onClick={handleCheckBoxClick}
                checked={isSelected}
                disabled={!documentFileUrl}
              />
            </Table.Td>
            <Table.Td>{no}</Table.Td>
            <Table.Td>{truncate(pj, 4)}</Table.Td>
            <Table.Td>{businessType}</Table.Td>
            <Table.Td style={{ minWidth: 0 }}>
              <div
                style={{
                  width: "100%",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {informationProvider}
              </div>
            </Table.Td>
            <Table.Td>{author}</Table.Td>
            <Table.Td>{formattedSubmissionDate}</Table.Td>
            <Table.Td>{documentStatusText}</Table.Td>
            <Table.Td>
              {documentFileUrl && (
                <ButtonWrapper>
                  <Button variant="primary" onClick={handleOpenPdf}>
                    조회
                  </Button>
                  <Button variant="tertiary" onClick={handleDownloadPdf}>
                    다운로드
                  </Button>
                  <Button variant="tertiary" onClick={handleCopyLink}>
                    링크복사
                  </Button>
                  <Toast.Message
                    open={isToastOpen}
                    onOpenChange={(open) => setIsToastOpen(open)}
                  >
                    링크가 복사되었습니다.
                  </Toast.Message>
                </ButtonWrapper>
              )}
            </Table.Td>
          </>
        ))}
    </Table.Tr>
  );
};

export default ContractTableRow;

const ButtonWrapper = styled.div`
  display: flex;
  gap: 1rem;
`;
